import {
  discardReconciliation,
  finishReconciliation,
  reopenReconciliation,
  setClearedLines,
  useGetReconciliation,
  type ReconciliationLineOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Checkbox, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { StatRow, StatTile } from '../../components/Stats';
import { TablePager, useTablePage } from '../../components/TablePager';
import { okData } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const BACK = '/staff/accounting/banking';

export function ReconciliationPage() {
  const { id } = useParams();
  const reconciliationId = Number(id);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = useCan('accounting.manage');
  const paging = useTablePage();
  const query = useGetReconciliation(reconciliationId);
  const rec = okData(query.data);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (query.isError) return <Alert severity="error">This statement couldn't be found.</Alert>;
  if (!rec) return <CircularProgress aria-label="Loading" />;

  const open = rec.status === 'open' && canManage;
  const lines = rec.lines;
  const showing = lines.slice(paging.page * paging.pageSize, (paging.page + 1) * paging.pageSize);
  const allTicked = lines.length > 0 && lines.every((line) => line.cleared);
  const difference = Number(rec.difference);

  const columns: DataColumn<ReconciliationLineOut>[] = [
    {
      label: 'On Statement',
      render: (line) => (
        <Checkbox
          size="small"
          checked={line.cleared}
          disabled={busy || !open}
          slotProps={{ input: { 'aria-label': `On statement: ${line.memo}` } }}
          onChange={(e) =>
            void run(() =>
              setClearedLines(reconciliationId, {
                line_ids: [line.line_id],
                cleared: e.target.checked,
              }),
            )
          }
        />
      ),
    },
    { label: 'Date', render: (line) => formatDate(line.entry_date) },
    { label: 'Description', render: (line) => line.memo },
    { label: 'Amount', align: 'right', render: (line) => formatMoney(line.amount) },
  ];

  return (
    <Stack spacing={2}>
      <div>
        <Button size="small" component={Link} to={BACK}>
          Back to Bank Reconciliation
        </Button>
      </div>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">{rec.bank_account_name}</Typography>
          <Typography color="text.secondary">
            Statement ending {formatDate(rec.statement_date)}
          </Typography>
        </div>
        {rec.status === 'finished' ? (
          <Chip color="success" label="Finished" />
        ) : (
          <Chip color="warning" label="In Progress" />
        )}
      </Stack>
      <StatRow>
        <StatTile label="Statement Balance" value={formatMoney(rec.statement_balance)} />
        <StatTile
          label="Ticked Balance"
          value={formatMoney(rec.cleared_balance)}
          hint={`From ${formatMoney(rec.opening_balance)} last statement`}
        />
        <StatTile label="Difference" value={formatMoney(rec.difference)} />
        <StatTile
          label="Outstanding"
          value={formatMoney(rec.outstanding)}
          hint="On the books, not on a statement yet"
        />
      </StatRow>
      {error && <Alert severity="error">{error}</Alert>}
      {open && difference !== 0 && (
        <Alert severity="warning">
          Tick each line that appears on the statement. You can finish when the difference is zero.
        </Alert>
      )}
      {rec.status === 'open' && (
        <>
          {open && lines.length > 0 && (
            <Stack direction="row">
              <Button
                size="small"
                disabled={busy}
                onClick={() =>
                  void run(() =>
                    setClearedLines(reconciliationId, {
                      line_ids: lines.map((line) => line.line_id),
                      cleared: !allTicked,
                    }),
                  )
                }
              >
                {allTicked ? 'Untick all' : 'Tick all'}
              </Button>
            </Stack>
          )}
          <DataTable columns={columns} rows={showing} rowKey={(line) => line.line_id} />
          <TablePager paging={paging} total={lines.length} />
          {lines.length === 0 && (
            <Alert severity="info">Nothing on the books up to this date is left to match.</Alert>
          )}
        </>
      )}
      {canManage && (
        <Stack direction="row" spacing={1}>
          {rec.status === 'open' ? (
            <>
              <Button
                variant="contained"
                disabled={busy || difference !== 0}
                onClick={() => void run(() => finishReconciliation(reconciliationId))}
              >
                Finish
              </Button>
              <Button
                color="error"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm('Discard this reconciliation and its ticks?')) return;
                  void run(async () => {
                    await discardReconciliation(reconciliationId);
                    await navigate(BACK);
                  });
                }}
              >
                Discard
              </Button>
            </>
          ) : (
            <Button
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Reopen this statement to change it?')) return;
                void run(() => reopenReconciliation(reconciliationId));
              }}
            >
              Reopen
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}
